<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import TheFilter from '@/components/category/organisms/TheFilter.vue'
import { TYPES as AdminToolTypes } from '@/constants/AdminTool'
import { ASSET_DOWNLOAD_URL } from '@/constants/CollectionPageConstants'
import {
  containsPrivateInfo,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import { useCollectionPageStore } from '@/stores/collectionPage'
import { useCustomerStore } from '@/stores/customer'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import DOMPurify from 'dompurify'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useRecommendationStore } from '../stores/recommendations.js'

const CategorySmartBlock = defineAsyncComponent(
  () => import('@/components/category/molecules/CategorySmartBlock.vue')
)
const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)
const MultiTools = defineAsyncComponent(
  () => import('@/components/basic/molecules/MultiTools.vue')
)
const Tool = defineAsyncComponent(
  () => import('@/components/basic/atoms/Tool.vue')
)

const collectionPageStore = useCollectionPageStore()
const searchConfig = useSearchConfig()
const route = useRoute()
const { customerIsAdmin } = storeToRefs(useCustomerStore())

const page = computed(() => collectionPageStore.page)
// Editorial HTML comes pre-rendered from the platform API (server-side TipTap →
// HTML). We sanitize defensively before v-html so a malformed or compromised
// payload cannot inject script tags or event handlers into the page.
const editorialHtml = computed(() =>
  page.value?.editorial_text
    ? DOMPurify.sanitize(page.value.editorial_text)
    : ''
)
const category = computed(() => searchConfig.category)
const blocks = computed(() => searchConfig.blocks)
const categoryError = computed(() => searchConfig.config.error)
const title = computed(() => page.value?.meta_title || page.value?.title || '')
const showPageTitle = computed(
  () => page.value?.show_title !== false && !!page.value?.title
)

const assetUrl = (id) => `${ASSET_DOWNLOAD_URL}${id}/download`
const hasTeaser = computed(
  () =>
    page.value?.desktop_asset_id ||
    page.value?.tablet_asset_id ||
    page.value?.mobile_asset_id
)
const teaserFallbackUrl = computed(() => {
  const p = page.value
  return assetUrl(
    p?.desktop_asset_id || p?.tablet_asset_id || p?.mobile_asset_id
  )
})

const recommendations = useRecommendationStore()
recommendations.fetch()

searchConfig.fetchCollectionPageProducts(route.params.id)

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (oldId && newId && newId !== oldId) {
      searchConfig.resetConfig()
      await nextTick()
      await searchConfig.fetchCollectionPageProducts(newId)
    }
  }
)

watch(
  () => route.query.q,
  async (newQ, oldQ) => {
    if (newQ !== oldQ) {
      searchConfig.resetConfig()
      if (!newQ) return
      await nextTick()
      await searchConfig.fetchCollectionPageProducts(route.params.id)
    }
  }
)

onBeforeRouteLeave(() => {
  collectionPageStore.$reset()
  searchConfig.resetConfig()
})

useHead({
  title,
  meta: [
    {
      name: 'robots',
      content: computed(() =>
        containsPrivateInfo() ? NO_INDEX_FOLLOW : page.value?.robots
      )
    },
    {
      name: 'description',
      content: computed(() => page.value?.meta_description)
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: computed(() => route.path)
    }
  ]
})
</script>

<template>
  <div
    class="position-relative"
    id="content"
    data-elbglobals="page_type:collection"
    data-elbcontext="shopping:discover"
  >
    <AppBanner />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader class="position-relative" />
    <main id="page-template">
      <div v-if="showPageTitle" class="v-container my-5">
        <div class="v-col-12 py-5">
          <h1
            class="font-garbata text-center font-size-xxxxl font-size-md-xxxxxl font-weight-bold c-winered"
          >
            {{ page.title }}
          </h1>
        </div>
      </div>
      <div v-if="hasTeaser || page.editorial_text" class="v-container mb-5">
        <div class="collection-page-card rounded">
          <picture v-if="hasTeaser">
            <source
              v-if="page.desktop_asset_id"
              :srcset="assetUrl(page.desktop_asset_id)"
              media="(min-width: 1280px)"
            />
            <source
              v-if="page.tablet_asset_id"
              :srcset="assetUrl(page.tablet_asset_id)"
              media="(min-width: 768px)"
            />
            <source
              v-if="page.mobile_asset_id"
              :srcset="assetUrl(page.mobile_asset_id)"
              media="(max-width: 767px)"
            />
            <img
              :src="teaserFallbackUrl"
              :alt="page.title"
              class="collection-page-card__image w-100"
            />
          </picture>
          <div
            v-if="editorialHtml"
            class="collection-page-card__editorial"
            v-html="editorialHtml"
          ></div>
        </div>
      </div>
      <p v-if="categoryError" class="text-center py-10">
        {{ $t('cms_loading_error') }}
      </p>
      <template v-else>
        <CategorySmartBlock
          v-if="category.code && blocks.before.html"
          :block="blocks.before"
        />
        <TheFilter v-if="category.code" />
      </template>
    </main>
    <LazyComponent id="footer" :delay="2000">
      <TheFooter />
    </LazyComponent>
    <MultiTools id="fixed-tools" v-if="customerIsAdmin">
      <Tool :type="AdminToolTypes.PRODUCT_FILTER_OVERRIDES" />
    </MultiTools>
  </div>
</template>

<style scoped>
.collection-page-card {
  overflow: hidden;
  background-color: #fff;
}

.collection-page-card__image {
  display: block;
}

.collection-page-card__editorial {
  padding: 24px;
}

.collection-page-card__editorial :deep(:first-child) {
  margin-top: 0;
}

.collection-page-card__editorial :deep(:last-child) {
  margin-bottom: 0;
}
</style>
